import React, { useState, useEffect, useCallback } from 'react';
import api from './services/api.js';
import { BrowserRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom';
import AdminPanel from './components/AdminPanel';
import AccountsPage from './components/AccountsPage';
import WithdrawalPoolPage from './components/WithdrawalPoolPage';
import WithdrawalRequestPage from './components/WithdrawalRequestPage';
import UsersPage from './components/UsersPage';
import DeliveriesPage from './components/DeliveriesPage';
import UserActivitiesPage from './components/UserActivitiesPage';
import BlacklistPage from './components/BlacklistPage';
import LoginPage from './components/LoginPage';
import SubcontractorsPage from './components/SubcontractorsPage';
import AccountingPage from './components/AccountingPage';
import SiteSettingsPage from './components/SiteSettingsPage';
import './App.css'; // CSS dosyasını dahil ediyoruz
import ApiKeysPage from './components/ApiKeysPage';
import ApiLogsPage from './components/ApiLogsPage';
import ApiDocsPage from './components/ApiDocsPage';
import PartnerLoginPage from './components/PartnerLoginPage';
import PartnerDashboardPage from './components/PartnerDashboardPage';
import PartnerManagementPage from './components/PartnerManagementPage';
import DataArchivePage from './components/DataArchivePage';
import ManagementUsersPage from './components/ManagementUsersPage';
import ManagementApprovalPage from './components/ManagementApprovalPage';
import AllDepositsPage from './components/AllDepositsPage';
import AllWithdrawalsPage from './components/AllWithdrawalsPage';


const checkPermission = (user, path, isManagement) => {
  if (!user) return false;
  const role = user.role;
  if (role === 'master_admin' || role === 'yonetim') return true;
  if (role === 'admin') {
    if (!isManagement) return true; // Departman admini kendi panelinde her şeye erişebilir
    const deniedForAdmin = [
      '/api-keys', '/api-logs', '/api-docs', '/partners',
      '/management-users'
    ];
    return !deniedForAdmin.includes(path);
  }
  if (role === 'finansci' || role === 'destek') {
    const allowedForFinansci = [
      '/subcontractors', '/admin', '/withdrawal-pool', '/withdrawal-request',
      '/accounts', '/deliveries', '/user-activities', '/blacklist',
      '/all-deposits', '/all-withdrawals', '/pending-approvals'
    ];
    return allowedForFinansci.includes(path);
  }
  if (isManagement && (role === 'yardimci' || role === 'izleyici')) {
    const shared = [
      '/subcontractors', '/admin', '/accounts', '/withdrawal-pool',
      '/withdrawal-request', '/deliveries', '/blacklist',
      '/all-deposits', '/all-withdrawals', '/pending-approvals'
    ];
    if (shared.includes(path)) return true;
    if (path === '/users') return role === 'yardimci';
    return false;
  }
  return (user.permissions || []).includes(path);
};

const ProtectedRoute = ({ children, path }) => {
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const isManagement = window.location.hostname.startsWith('yonetim');
  if (!user) return <Navigate to="/login" replace />;
  if (!checkPermission(user, path, isManagement)) return <div style={{ padding: 20, color: 'white' }}></div>;
  return children;
};

function App() {
  const [isActive, setIsActive] = useState(false);
  // Kullanıcı bilgisini localStorage'dan alarak oturumu koruyoruz
  const [user, setUser] = useState(JSON.parse(localStorage.getItem('user')));
  const [darkMode, setDarkMode] = useState(localStorage.getItem('darkMode') === 'true');

  // Bildirim State'i
  const [notificationsEnabled, setNotificationsEnabled] = useState(localStorage.getItem('notificationsEnabled') === 'true');

  // Subdomain Kontrolü (yonetim.siteadi.com gibi)
  const isManagement = window.location.hostname.startsWith('yonetim');
  const isPartner = window.location.hostname.startsWith('partner');

  // Yönetim Paneli için State'ler
  const [panels, setPanels] = useState([]);
  const [expandedPanelId, setExpandedPanelId] = useState(null);
  const [selectedPanelId, setSelectedPanelId] = useState(() => localStorage.getItem('selectedPanelId') || null);

  // Panelleri Çek (Sadece Yönetim Panelinde) — her zaman güncel tutulur
  const fetchPanels = useCallback(() => {
    if (isManagement && user) {
      api.getPanels()
        .then(res => {
          const list = res.data || [];
          setPanels(prev => {
            const prevIds = (prev || []).map(p => p._id + ':' + p.name + ':' + p.isActive).join(',');
            const nextIds = list.map(p => p._id + ':' + p.name + ':' + p.isActive).join(',');
            if (prevIds === nextIds) return prev;
            return list;
          });
          setSelectedPanelId(prev => (prev && !list.some(p => p.panelId === prev) ? null : prev));
          setExpandedPanelId(prev => (prev && !list.some(p => p.panelId === prev) ? null : prev));
        })
        .catch(err => console.error(err));
    }
  }, [isManagement, user]);

  useEffect(() => {
    fetchPanels();
    const handlePanelsUpdated = () => fetchPanels();
    window.addEventListener('panelsUpdated', handlePanelsUpdated);
    return () => {
      window.removeEventListener('panelsUpdated', handlePanelsUpdated);
    };
  }, [fetchPanels]);

  // Bildirim Polling (Sürekli Kontrol)
  useEffect(() => {
    if (!user || !notificationsEnabled) return;

    let isMounted = true;
    let firstLoad = true;

    const checkNotifications = async () => {
      try {
        const activePanelId = selectedPanelId || user?.panelId;
        const [invRes, withRes] = await Promise.all([
          api.getInvestments({ status: 'new', panelId: activePanelId || '' }),
          api.getWithdrawals({ status: 'new', panelId: activePanelId || '' })
        ]);

        if (!isMounted) return;

        // LocalStorage'dan bildirim geçmişini al (Sekmeler arası senkronizasyon için)
        let notifiedIds = JSON.parse(localStorage.getItem('notifiedIds') || '[]');
        let hasChanges = false;

        const handleNotification = (req, title, body) => {
          if (!notifiedIds.includes(req._id)) {
            if (!firstLoad) {
              new Notification(title, { body });
            }
            notifiedIds.push(req._id);
            hasChanges = true;
          }
        };

        // Yatırım Kontrolü
        invRes.data.forEach(req => {
          handleNotification(req, "💰 Yeni Yatırım Talebi", `${req.amount} TL tutarında yeni bir yatırım isteği geldi.`);
        });

        // Çekim Kontrolü
        withRes.data.forEach(req => {
          handleNotification(req, "💸 Yeni Çekim Talebi", `${req.amount} TL tutarında yeni bir çekim isteği geldi.`);
        });

        // Eski ID'leri silmek yerine listeyi kaydırıyoruz, böylece anlık veri kayıplarında tekrar bildirim gitmez.
        if (notifiedIds.length > 50) {
          notifiedIds = notifiedIds.slice(notifiedIds.length - 50);
          hasChanges = true;
        }

        if (hasChanges) {
          localStorage.setItem('notifiedIds', JSON.stringify(notifiedIds));
        }

        firstLoad = false;
      } catch (error) {
        console.error("Bildirim kontrol hatası:", error);
      }
    };

    checkNotifications(); // İlk çalıştırma
    const interval = setInterval(checkNotifications, 5000); // 5 saniyede bir kontrol

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [user, notificationsEnabled, selectedPanelId]);

  const toggleTheme = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem('darkMode', newMode);
  };

  const toggleNotifications = () => {
    if (!notificationsEnabled) {
      if (!("Notification" in window)) {
        alert("Tarayıcınız bildirimleri desteklemiyor.");
        return;
      }
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          setNotificationsEnabled(true);
          localStorage.setItem('notificationsEnabled', 'true');
          new Notification("Bildirimler Açıldı", { body: "Artık yeni taleplerden haberdar olacaksınız." });
        }
      });
    } else {
      setNotificationsEnabled(false);
      localStorage.setItem('notificationsEnabled', 'false');
    }
  };

  const toggleSidebar = () => {
    setIsActive(!isActive);
  };

  const handleLogin = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('user');
  };

  // Token Kontrolü: Eğer kullanıcı var ama token yoksa (Eski oturum), çıkış yap.
  useEffect(() => {
    if (user && !user.token) {
      handleLogout();
    }
  }, [user]);

  // Yetki kontrol fonksiyonu (sidebar linkleri için)
  const hasPermission = (path) => checkPermission(user, path, isManagement);

  const themeStyles = {
    '--bg-color': darkMode ? '#121212' : '#f4f7f6',
    '--card-bg': darkMode ? '#1e1e1e' : '#ffffff',
    '--text-color': darkMode ? '#e0e0e0' : '#333333',
    '--border-color': darkMode ? '#444444' : '#eeeeee',
    '--input-border': darkMode ? '#555' : '#ccc',
    '--input-bg': darkMode ? '#2d2d2d' : '#ffffff',
    '--table-header-bg': darkMode ? '#2d2d2d' : '#f8f9fa',
    '--table-row-even': darkMode ? '#2d2d2d' : '#f8f9fa',
    '--table-row-odd': darkMode ? '#1e1e1e' : '#ffffff',
    '--shadow': darkMode ? '0 4px 20px rgba(0,0,0,0.4)' : '0 4px 20px rgba(0,0,0,0.08)',
    '--warning-bg': darkMode ? '#423a20' : '#fff3cd',
    minHeight: '100vh',
    backgroundColor: 'var(--bg-color)',
    color: 'var(--text-color)',
    transition: 'background-color 0.3s, color 0.3s'
  };

  const togglePanelAccordion = (id) => {
    setExpandedPanelId(expandedPanelId === id ? null : id);
  };

  const handlePanelSelect = (pId) => {
    setSelectedPanelId(pId);
    if (pId) localStorage.setItem('selectedPanelId', pId);
    else localStorage.removeItem('selectedPanelId');
  };

  // Yönetim Paneli Menüsü
  const renderManagementMenu = () => (
    <ul className="components">
      <p className="sidebar-heading">DEPARTMANLAR</p>

      {/* Departman Yönetimi Sayfası Linki */}
      <li>
        <Link to="/subcontractors">
          <i className="fas fa-cogs"></i>
          <span>Departman Yönetimi</span>
        </Link>
      </li>

      {/* Dinamik Panel Listesi (Akordiyon) */}
      {panels.map(panel => (
        <li key={panel._id}>
          <a href="#" onClick={(e) => { e.preventDefault(); togglePanelAccordion(panel.panelId); }} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span><i className="fas fa-user-tie" style={{ marginRight: '10px' }}></i> {panel.name || panel.panelId}</span>
            <i className={`fas fa-chevron-${expandedPanelId === panel.panelId ? 'up' : 'down'}`} style={{ fontSize: '0.8em', transition: 'transform 0.3s' }}></i>
          </a>
          <ul style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            maxHeight: expandedPanelId === panel.panelId ? '1000px' : '0',
            opacity: expandedPanelId === panel.panelId ? 1 : 0,
            overflow: 'hidden',
            transition: 'all 0.4s ease-in-out',
            paddingLeft: '15px',
            background: 'rgba(0,0,0,0.2)'
          }}>
            {hasPermission('/admin') && (
            <li>
              <Link to="/admin" onClick={() => handlePanelSelect(panel.panelId)}>
                <i className="fas fa-plus"></i> Yatırım Havuzu
              </Link>
            </li>
            )}
            {hasPermission('/withdrawal-pool') && (
            <li>
              <Link to="/withdrawal-pool" onClick={() => handlePanelSelect(panel.panelId)}>
                <i className="fas fa-minus"></i> Çekim Havuzu
              </Link>
            </li>
            )}
            {hasPermission('/withdrawal-request') && (
            <li>
              <Link to="/withdrawal-request" onClick={() => handlePanelSelect(panel.panelId)}>
                <i className="fas fa-file-invoice"></i> Çekim Talebi Al
              </Link>
            </li>
            )}
            {hasPermission('/accounts') && (
            <li>
              <Link to="/accounts" onClick={() => handlePanelSelect(panel.panelId)}>
                <i className="fas fa-list"></i> Hesaplar
              </Link>
            </li>
            )}
            {hasPermission('/users') && (
            <li>
              <Link to="/users" onClick={() => handlePanelSelect(panel.panelId)}>
                <i className="fas fa-users"></i> Kullanıcı
              </Link>
            </li>
            )}
            {hasPermission('/deliveries') && (
            <li>
              <Link to="/deliveries" onClick={() => handlePanelSelect(panel.panelId)}>
                <i className="fas fa-box-open"></i> Teslimler
              </Link>
            </li>
            )}
            {hasPermission('/blacklist') && (
            <li>
              <Link to="/blacklist" onClick={() => handlePanelSelect(panel.panelId)}>
                <i className="fas fa-ban"></i> Black List
              </Link>
            </li>
            )}
          </ul>
        </li>
      ))}
      {(hasPermission('/all-deposits') || hasPermission('/all-withdrawals')) && (
        <>
          <div className="sidebar-divider"></div>
          <p className="sidebar-heading">TÜM İŞLEMLER</p>
          {hasPermission('/all-deposits') && (
            <li><Link to="/all-deposits"><i className="fas fa-arrow-down"></i><span>Tüm Yatırımlar</span></Link></li>
          )}
          {hasPermission('/all-withdrawals') && (
            <li><Link to="/all-withdrawals"><i className="fas fa-arrow-up"></i><span>Tüm Çekimler</span></Link></li>
          )}
        </>
      )}

      {(hasPermission('/api-keys') || hasPermission('/api-logs') || hasPermission('/api-docs') || hasPermission('/partners')) && (
        <>
          <div className="sidebar-divider"></div>
          <p className="sidebar-heading">Entegrasyon</p>
          {hasPermission('/api-keys') && (
            <li><Link to="/api-keys"><i className="fas fa-key"></i><span>API Anahtarları</span></Link></li>
          )}
          {hasPermission('/api-logs') && (
            <li><Link to="/api-logs"><i className="fas fa-list-alt"></i><span>API Logları</span></Link></li>
          )}
          {hasPermission('/api-docs') && (
            <li><Link to="/api-docs"><i className="fas fa-book"></i><span>API Dokümantasyon</span></Link></li>
          )}
          {hasPermission('/partners') && (
            <li><Link to="/partners"><i className="fas fa-handshake"></i><span>Partner Yönetimi</span></Link></li>
          )}
        </>
      )}

      {(hasPermission('/accounting') || hasPermission('/management-users') || hasPermission('/pending-approvals') || hasPermission('/site-settings') || hasPermission('/archive')) && (
        <>
          <div className="sidebar-divider"></div>
          <p className="sidebar-heading">YÖNETİM</p>
          {hasPermission('/accounting') && (
            <li><Link to="/accounting"><i className="fas fa-calculator"></i><span>Muhasebe</span></Link></li>
          )}
          {hasPermission('/management-users') && (
            <li><Link to="/management-users"><i className="fas fa-user-shield"></i><span>Yönetim Kullanıcıları</span></Link></li>
          )}
          {hasPermission('/pending-approvals') && (
            <li><Link to="/pending-approvals"><i className="fas fa-clipboard-check"></i><span>Yüksek Çekimler</span></Link></li>
          )}
          {hasPermission('/site-settings') && (
            <li><Link to="/site-settings"><i className="fas fa-cogs"></i><span>Site Ayarları</span></Link></li>
          )}
          {hasPermission('/archive') && (
            <li><Link to="/archive"><i className="fas fa-archive"></i><span>Veri Arşivleme</span></Link></li>
          )}
        </>
      )}
      {renderLogoutButton()}
    </ul>
  );

  // Partner Subdomain Yönlendirmesi
  if (isPartner) {
    return (
      <Router>
        <Routes>
          <Route path="/login" element={<PartnerLoginPage />} />
          <Route path="/dashboard" element={<PartnerDashboardPage />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Router>
    );
  }

  return (
    <Router>
      <Routes>
        {/* Herkese Açık Sayfalar */}
        <Route path="/login" element={!user ? <LoginPage onLogin={handleLogin} defaultRedirect={isManagement ? "/" : "/admin"} /> : <Navigate to={isManagement ? "/" : "/admin"} />} />

        {/* Partner Sayfaları */}
        <Route path="/partner/login" element={<PartnerLoginPage />} />
        <Route path="/partner/dashboard" element={<PartnerDashboardPage />} />

        {/* Giriş Yapılmış Alanlar (Sidebar + İçerik) */}
        <Route path="/*" element={
          !user ? <Navigate to="/login" replace /> : (
            <div className="wrapper" style={themeStyles}>
              {/* --- SIDEBAR --- */}
              <nav id="sidebar" className={isActive ? 'active' : ''}>
                <div className="sidebar-header">
                  <img src="/payzopay.png" alt="PayzoPay" style={{ maxWidth: '170px', width: '100%', height: 'auto', display: 'block', margin: '0 auto' }} />
                  <div style={{ fontSize: '0.8em', color: '#aaa', marginTop: '5px' }}>{user.username}</div>
                </div>

                {isManagement ? renderManagementMenu() : (
                  // --- MEVCUT PANEL MENÜSÜ ---
                  <ul className="components">
                    <p className="sidebar-heading">MENÜ</p>

                    {hasPermission('/admin') && (
                      <li>
                        <Link to="/admin">
                          <i className="fas fa-plus"></i>
                          <span>Yatırım Havuzu</span>
                        </Link>
                      </li>
                    )}
                    {hasPermission('/withdrawal-pool') && (
                      <li>
                        <Link to="/withdrawal-pool">
                          <i className="fas fa-minus"></i>
                          <span>Çekim Havuzu</span>
                        </Link>
                      </li>
                    )}
                    {hasPermission('/withdrawal-request') && (
                      <li>
                        <Link to="/withdrawal-request">
                          <i className="fas fa-file-invoice"></i>
                          <span>Çekim Talebi Al</span>
                        </Link>
                      </li>
                    )}
                    {hasPermission('/accounts') && (
                      <li>
                        <Link to="/accounts">
                          <i className="fas fa-list"></i>
                          <span>Hesaplar</span>
                        </Link>
                      </li>
                    )}

                    <div className="sidebar-divider"></div>
                    <p className="sidebar-heading">Kullanıcı ve Rapor</p>

                    {hasPermission('/users') && (
                      <li>
                        <Link to="/users">
                          <i className="fas fa-users"></i>
                          <span>Kullanıcı</span>
                        </Link>
                      </li>
                    )}
                    {hasPermission('/deliveries') && (
                      <li>
                        <Link to="/deliveries">
                          <i className="fas fa-box-open"></i>
                          <span>Teslimler</span>
                        </Link>
                      </li>
                    )}
                    {hasPermission('/user-activities') && (
                      <li>
                        <Link to="/user-activities">
                          <i className="fas fa-history"></i>
                          <span>Kullanıcı Hareketleri</span>
                        </Link>
                      </li>
                    )}
                    {renderLogoutButton()}
                  </ul>
                )}

                <div className="sidebar-bottom">
                  <button type="button" className="btn-toggle" onClick={toggleSidebar}>
                    <i className="fas fa-chevron-left"></i>
                  </button>
                </div>
              </nav>

              {/* --- İÇERİK ALANI --- */}
              <div id="content">
                <nav className="navbar" style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px 20px', background: 'transparent', boxShadow: 'none', borderBottom: 'none' }}>
                  <button
                    onClick={toggleTheme}
                    title={darkMode ? 'Açık Tema' : 'Koyu Tema'}
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      border: 'none',
                      backgroundColor: darkMode ? '#f1c40f' : '#343a40',
                      color: darkMode ? '#333' : '#fff',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                      marginRight: '10px'
                    }}
                  >
                    <i className={`fas ${darkMode ? 'fa-sun' : 'fa-moon'}`} style={{ fontSize: '1.2em' }}></i>
                  </button>
                  <button
                    onClick={toggleNotifications}
                    title={notificationsEnabled ? 'Bildirimleri Kapat' : 'Bildirimleri Aç'}
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      border: 'none',
                      backgroundColor: notificationsEnabled ? '#28a745' : '#6c757d',
                      color: '#fff',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
                    }}
                  >
                    <i className={`fas ${notificationsEnabled ? 'fa-bell' : 'fa-bell-slash'}`} style={{ fontSize: '1.2em' }}></i>
                  </button>
                </nav>

                <Routes>
                  {isManagement ? (
                    // --- YÖNETİM PANELİ ROTALARI ---
                    <>
                      <Route path="/" element={<Navigate to="/subcontractors" replace />} />
                      <Route path="/subcontractors" element={<ProtectedRoute path="/subcontractors"><SubcontractorsPage /></ProtectedRoute>} />
                      <Route path="/accounting" element={<ProtectedRoute path="/accounting"><AccountingPage /></ProtectedRoute>} />
                      <Route path="/management-users" element={<ProtectedRoute path="/management-users"><ManagementUsersPage /></ProtectedRoute>} />
                      <Route path="/pending-approvals" element={<ProtectedRoute path="/pending-approvals"><ManagementApprovalPage /></ProtectedRoute>} />
                      <Route path="/all-deposits" element={<ProtectedRoute path="/all-deposits"><AllDepositsPage /></ProtectedRoute>} />
                      <Route path="/all-withdrawals" element={<ProtectedRoute path="/all-withdrawals"><AllWithdrawalsPage /></ProtectedRoute>} />
                      <Route path="/site-settings" element={<ProtectedRoute path="/site-settings"><SiteSettingsPage /></ProtectedRoute>} />
                      <Route path="/admin" element={<ProtectedRoute path="/admin"><AdminPanel panelId={selectedPanelId} /></ProtectedRoute>} />
                      <Route path="/accounts" element={<ProtectedRoute path="/accounts"><AccountsPage panelId={selectedPanelId} /></ProtectedRoute>} />
                      <Route path="/withdrawal-pool" element={<ProtectedRoute path="/withdrawal-pool"><WithdrawalPoolPage panelId={selectedPanelId} /></ProtectedRoute>} />
                      <Route path="/withdrawal-request" element={<ProtectedRoute path="/withdrawal-request"><WithdrawalRequestPage panelId={selectedPanelId} /></ProtectedRoute>} />
                      <Route path="/users" element={<ProtectedRoute path="/users"><UsersPage panelId={selectedPanelId} /></ProtectedRoute>} />
                      <Route path="/deliveries" element={<ProtectedRoute path="/deliveries"><DeliveriesPage panelId={selectedPanelId} /></ProtectedRoute>} />
                      <Route path="/user-activities" element={<ProtectedRoute path="/user-activities"><UserActivitiesPage panelId={selectedPanelId} /></ProtectedRoute>} />
                      <Route path="/blacklist" element={<ProtectedRoute path="/blacklist"><BlacklistPage panelId={selectedPanelId} /></ProtectedRoute>} />
                      <Route path="/api-keys" element={<ProtectedRoute path="/api-keys"><ApiKeysPage /></ProtectedRoute>} />
                      <Route path="/api-logs" element={<ProtectedRoute path="/api-logs"><ApiLogsPage /></ProtectedRoute>} />
                      <Route path="/api-docs" element={<ProtectedRoute path="/api-docs"><ApiDocsPage /></ProtectedRoute>} />
                      <Route path="/partners" element={<ProtectedRoute path="/partners"><PartnerManagementPage /></ProtectedRoute>} />
                      <Route path="/archive" element={<ProtectedRoute path="/archive"><DataArchivePage /></ProtectedRoute>} />

                      <Route path="*" element={<Navigate to="/subcontractors" replace />} />
                    </>
                  ) : (
                    // --- MEVCUT PANEL ROTALARI ---
                    <>
                      <Route path="/" element={<Navigate to="/admin" replace />} />
                      <Route path="/admin" element={<ProtectedRoute path="/admin"><AdminPanel panelId={selectedPanelId} /></ProtectedRoute>} />
                      <Route path="/accounts" element={<ProtectedRoute path="/accounts"><AccountsPage panelId={selectedPanelId} /></ProtectedRoute>} />
                      <Route path="/withdrawal-pool" element={<ProtectedRoute path="/withdrawal-pool"><WithdrawalPoolPage panelId={selectedPanelId} /></ProtectedRoute>} />
                      <Route path="/withdrawal-request" element={<ProtectedRoute path="/withdrawal-request"><WithdrawalRequestPage panelId={selectedPanelId} /></ProtectedRoute>} />
                      <Route path="/users" element={<ProtectedRoute path="/users"><UsersPage panelId={selectedPanelId} /></ProtectedRoute>} />
                      <Route path="/deliveries" element={<ProtectedRoute path="/deliveries"><DeliveriesPage panelId={selectedPanelId} /></ProtectedRoute>} />
                      <Route path="/user-activities" element={<ProtectedRoute path="/user-activities"><UserActivitiesPage panelId={selectedPanelId} /></ProtectedRoute>} />
                      <Route path="*" element={<Navigate to="/admin" replace />} />
                    </>
                  )}
                </Routes>
              </div>
            </div>
          )
        } />
      </Routes>
    </Router>
  );

  function renderLogoutButton() {
    return (
      <li>
        <a href="#" onClick={(e) => { e.preventDefault(); handleLogout(); }}>
          <i className="fas fa-sign-out-alt" style={{ marginRight: '10px' }}></i>
          <span>Çıkış Yap</span>
        </a>
      </li>
    );
  }
}

export default App;
